// Region routing for *.docbits.com services.
//
// Region is decided once per session from the /profile call after login,
// stored encrypted in LocalStorage, and baked into every URL constant at
// module-load time (see APIs.ts). The post-/profile call never reloads —
// URL constants are correct for the persisted region from prior sessions,
// and a first-time login self-corrects on the next page load. A LATER
// region change (future sub-org switch) reloads so constants rebuild —
// no listeners, no Proxy, no per-request rewriting.

import CryptoJS from 'crypto-js';
import { LocalStorage } from 'quasar';
import encryptionUtil from 'src/utils/encryptionUtil';

// The encryption helpers live on the legacy Vue mixin's `methods` block.
// They don't reference `this`, so we can call them straight off the default
// export — no need to instantiate the mixin or duplicate the primitives.
// Accessed lazily at the call sites below so tests that stub `encryptionUtil`
// with `{ default: {} }` (export-configuration suite) don't crash on import.

export const REGION_STORAGE_KEY = 'api_region';
export const AUTH_REGION_STORAGE_KEY = 'auth_region';

export type ApiRegion = 'eu' | 'us';

// Stages that participate in region routing. Adding a stage here is enough
// to opt it in; everything else (stage, demo, local) stays unprefixed.
// prod uses APP_VUE_BRANCH='' so the URL is https://<region>.<service>.docbits.com.
export const REGIONAL_STAGES = ['prod', 'dev', 'sandbox'] as const;

// Every *.docbits.com service that gets the region prefix on regional
// stages. Used as the source of truth for the contract spec and any future
// service-list rendering. auth is intentionally NOT in this list.
export const REGIONAL_SERVICES = [
  'api',
  'api-sticky',
  'docflow',
  'operator',
  'idea',
  'autoaccounting',
  'pomatch',
  'import-ftp',
  'import-email',
  'docnet',
  'docnet-sticky',
  'fulltext',
] as const;

const stage = process.env.APP_VUE_STAGE || '';
const rawBranch = process.env.APP_VUE_BRANCH || '';

const branch =
  rawBranch === 'ruiz' || rawBranch === 'prod'
    ? ''
    : rawBranch
      ? rawBranch + '.'
      : '';

export function isRegionalStage(): boolean {
  return (REGIONAL_STAGES as readonly string[]).includes(stage);
}

/**
 * The branch subdomain prefix for *.docbits.com hosts.
 *
 * Returns `'<branch>.'` for non-production branches (e.g. `'dev.'`,
 * `'stage.'`) and `''` for production (`prod`/`ruiz`) so prod hosts stay
 * clean — e.g. `inbound.docbits.com`, not `prod.inbound.docbits.com`.
 */
export function getBranchPrefix(): string {
  return branch;
}

/**
 * Decrypts the persisted region or falls back to 'eu' if nothing is
 * stored, the value is tampered (plaintext), or decryption returns
 * an unrecognised value.
 */
export function getCurrentApiRegion(): ApiRegion {
  const stored = LocalStorage.getItem(REGION_STORAGE_KEY) as string | null;
  if (!stored) {
    return 'eu';
  }

  let plain = '';
  try {
    plain = encryptionUtil.methods
      .generalDecryption(stored)
      .toString(CryptoJS.enc.Utf8);
  } catch {
    // Tampered or non-ciphertext value → fall through to default.
  }
  if (plain === 'us' || plain === 'eu') {
    return plain;
  }

  return 'eu';
}

/**
 * Build the URL for a *.docbits.com service:
 *   regional stage  →  https://<region>.<branch><service>.docbits.com
 *   other stage     →  https://<branch><service>.docbits.com
 *
 * `<service>` is the bare subdomain — e.g. `'api'`, `'operator'`,
 * `'fulltext'` — and must be one of REGIONAL_SERVICES.
 */
export function buildServiceUrl(service: string): string {
  const host = `${branch}${service}.docbits.com`;

  if (!isRegionalStage()) {
    return `https://${host}`;
  }

  const region = getCurrentApiRegion();
  return `https://${region}.${host}`;
}

export function getCurrentAuthRegion(): ApiRegion {
  const stored = LocalStorage.getItem(AUTH_REGION_STORAGE_KEY) as
    | string
    | null;
  return stored === 'us' ? 'us' : 'eu';
}

export function setAuthRegion(region: ApiRegion): void {
  LocalStorage.set(AUTH_REGION_STORAGE_KEY, region === 'us' ? 'us' : 'eu');
}

export function clearPersistedAuthRegion(): void {
  LocalStorage.remove(AUTH_REGION_STORAGE_KEY);
}

export function buildAuthUrl(regionOverride?: ApiRegion): string {
  const authBranch = branch.startsWith('pre-prod') ? '' : branch;
  if (!isRegionalStage()) {
    return `https://${authBranch}auth.docbits.com`;
  }
  const region = regionOverride ?? getCurrentAuthRegion();
  return `https://${region}.${authBranch}auth.docbits.com`;
}

let lastSetRegion: ApiRegion | null = null;

/**
 * Persist the new region (encrypted). The first successful call in a
 * session (post-/profile handoff) never reloads. A later call that
 * flips the region triggers a full page reload so URL constants
 * rebuild — reserved for sub-org switches. Encrypt or storage failures
 * are logged and leave the prior state intact.
 */
export function setApiRegion(region: ApiRegion | undefined | null): void {
  const resolved: ApiRegion = region === 'us' ? 'us' : 'eu';

  try {
    LocalStorage.set(
      REGION_STORAGE_KEY,
      String(encryptionUtil.methods.generalEncryption(resolved)),
    );
  } catch (error) {
    console.error('[Region] Failed to persist api_region:', error);
    return;
  }

  const isFirstCallThisSession = lastSetRegion === null;
  const regionDidChange =
    !isFirstCallThisSession && resolved !== lastSetRegion;
  lastSetRegion = resolved;

  if (regionDidChange && typeof window !== 'undefined') {
    window.location.reload();
  }
}

export function clearPersistedApiRegion(): void {
  LocalStorage.remove(REGION_STORAGE_KEY);
}

// Convenience constants. Co-located so the listener-pattern shims below
// can hand back a URL without importing from APIs.ts (which would create
// a circular dependency).
export const DOC2API_URL = buildServiceUrl('api');
export const WEB_SOCKET_URL = buildServiceUrl('api-sticky');

export function buildFaroCollectorUrl(): string {
  if (stage === 'local') {
    return '';
  }
  return `${buildServiceUrl('faro')}/collect`;
}

// ─── Transitional no-op shims ────────────────────────────────────────────
//
// In the new model the URL is correct at module load and a region change
// reloads the page, so per-call gating and listener callbacks are no
// longer load-bearing. These exports keep existing consumers compiling
// until a follow-up commit prunes the call sites.
//
// `whenRegionReady` resolves immediately because region is either
// persisted (loaded synchronously) or already defaulted to 'eu'.

export function whenRegionReady(): Promise<ApiRegion> {
  return Promise.resolve(getCurrentApiRegion());
}

export function isRegionReady(): boolean {
  return true;
}

export function onApiRegionChange(cb: (region: ApiRegion) => void): void {
  cb(getCurrentApiRegion());
}

export function onApiBaseUrlChange(cb: (url: string) => void): void {
  cb(DOC2API_URL);
}

export function persistApiRegion(region: ApiRegion | undefined | null): void {
  setApiRegion(region);
}
